// src/services/NotificationService.js
// Aggregates system notifications from deadlines and stale data.
// Stores read status in StorageAdapter. Subscribe pattern for reactivity.

import storage from './StorageAdapter';
import periodicReportingService from './PeriodicReportingService';
import bwraService from './BWRAService';
import scheduledValidationService from './ScheduledValidationService';
import auditService from './AuditService';
import kpiThresholdService from './KpiThresholdService';
import {
  getModuleLastUpdated,
  getStalenessStatus,
  MODULE_STALENESS_THRESHOLDS,
} from '../modules/Dashboard/utils/stalenessUtils';
import {
  getGeoRiskStats,
  getBwraStats,
  getPeriodicReportingStats,
} from '../modules/Dashboard/utils/dashboardStats';

const WARNING_DAYS = 30;
const LOOKAHEAD_DAYS = 60;
const READ_STATUS_KEY = 'rms_notification_read_status';
const ASSIGNEE_NOTIFICATIONS_KEY = 'rms_assignee_notifications';
const NOTIFICATION_SETTINGS_KEY = 'rms_notification_settings';
const WEBHOOK_SETTINGS_KEY = 'rms_webhook_settings';

// Webhook event types configurable by admin
export const WEBHOOK_EVENT_TYPES = [
  { id: 'approvalGranted', defaultEnabled: true },
  { id: 'ruleChanged', defaultEnabled: true },
  { id: 'newRiskAdded', defaultEnabled: false },
  { id: 'reportCreated', defaultEnabled: true },
  { id: 'validationCompleted', defaultEnabled: false },
];

// Notification types that can be configured per role
export const NOTIFICATION_TYPES = [
  { id: 'deadlines', defaultEnabled: true, defaultFrequency: 'immediate' },
  { id: 'staleData', defaultEnabled: true, defaultFrequency: 'immediate' },
  { id: 'scheduledValidations', defaultEnabled: true, defaultFrequency: 'immediate' },
  { id: 'assigneeNotifications', defaultEnabled: true, defaultFrequency: 'immediate' },
  { id: 'kpiThresholds', defaultEnabled: true, defaultFrequency: 'immediate' },
];

export const NOTIFICATION_FREQUENCIES = ['immediate', 'daily', 'weekly'];

const DEFAULT_ROLE_SETTINGS = (roleId) => {
  const settings = {};
  NOTIFICATION_TYPES.forEach(({ id, defaultEnabled, defaultFrequency }) => {
    settings[id] = { enabled: defaultEnabled, frequency: defaultFrequency };
  });
  return settings;
};

const BUILT_IN_ROLES = ['admin', 'analyst', 'viewer'];

const MODULE_ROUTES = {
  GeoRisk: '/geo-risk',
  CustomerRisk: '/customer-risk',
  BWRA: '/bwra',
  TransactionMonitoring: '/transaction-monitoring',
  PeriodicReporting: '/periodic-reporting',
};

class NotificationService {
  constructor() {
    this.listeners = [];
    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => this.notifyListeners());
    }
  }

  _getReadStatus() {
    return storage.getJSON(READ_STATUS_KEY, {});
  }

  async _setReadStatus(status) {
    await storage.setJSON(READ_STATUS_KEY, status);
  }

  _computeRaw() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const notifications = [];

    // 1a. Periodic Reporting annual report deadline
    try {
      const currentYear = now.getFullYear();
      const reportYear = currentYear - 1;
      const dueDate = new Date(currentYear, 2, 31); // March 31
      const daysUntil = Math.round((dueDate - now) / (1000 * 60 * 60 * 24));
      const report = periodicReportingService.getReportByYear(reportYear);
      if (report?.status !== 'approved' && daysUntil <= LOOKAHEAD_DAYS) {
        notifications.push({
          id: `prpt_${reportYear}`,
          type: 'deadlines',
          category: daysUntil < 0 ? 'urgent' : 'warning',
          titleKey: 'notifications.annualReport',
          titleParams: { year: reportYear },
          descKey: daysUntil < 0 ? 'notifications.overdueDesc' : 'notifications.dueSoonDesc',
          descParams: { count: Math.abs(daysUntil) },
          route: '/periodic-reporting',
          daysUntil,
        });
      }
    } catch (e) { /* skip */ }

    // 1b. BWRA annual review
    try {
      const versions = bwraService.getVersions();
      if (versions.length > 0) {
        const latestVersion = versions[versions.length - 1];
        const lastSaved = new Date(latestVersion.createdAt);
        lastSaved.setHours(0, 0, 0, 0);
        const dueDate = new Date(lastSaved);
        dueDate.setFullYear(dueDate.getFullYear() + 1);
        const daysUntil = Math.round((dueDate - now) / (1000 * 60 * 60 * 24));
        if (daysUntil <= LOOKAHEAD_DAYS) {
          notifications.push({
            id: 'bwra_annual_review',
            type: 'deadlines',
            category: daysUntil < 0 ? 'urgent' : 'warning',
            titleKey: 'notifications.bwraReview',
            titleParams: {},
            descKey: daysUntil < 0 ? 'notifications.overdueDesc' : 'notifications.dueSoonDesc',
            descParams: { count: Math.abs(daysUntil) },
            route: '/bwra',
            daysUntil,
          });
        }
      }
    } catch (e) { /* skip */ }

    // 1c. Scheduled validations
    try {
      const schedules = scheduledValidationService.getSchedules();
      Object.entries(schedules).forEach(([modelKey, schedule]) => {
        if (!schedule.enabled || !schedule.nextRun) return;
        const dueDate = new Date(schedule.nextRun);
        dueDate.setHours(0, 0, 0, 0);
        const daysUntil = Math.round((dueDate - now) / (1000 * 60 * 60 * 24));
        if (daysUntil <= LOOKAHEAD_DAYS) {
          const modelName =
            schedule.modelName ||
            (modelKey === 'georisk' ? 'GeoRisk' : modelKey.replace('customerrisk_', ''));
          notifications.push({
            id: `validation_${modelKey}`,
            type: 'scheduledValidations',
            category: daysUntil < 0 ? 'urgent' : daysUntil <= WARNING_DAYS ? 'warning' : 'info',
            titleKey: 'notifications.scheduledValidation',
            titleParams: { model: modelName },
            descKey: daysUntil < 0 ? 'notifications.overdueDesc' : 'notifications.dueSoonDesc',
            descParams: { count: Math.abs(daysUntil) },
            route: '/admin?tab=validation',
            daysUntil,
          });
        }
      });
    } catch (e) { /* skip */ }

    // 2. Stale data notifications (red status only)
    try {
      const logs = auditService.getLogs();
      Object.keys(MODULE_STALENESS_THRESHOLDS).forEach(mod => {
        const thresholds = MODULE_STALENESS_THRESHOLDS[mod];
        const lastUpdated = getModuleLastUpdated(mod, logs);
        const status = getStalenessStatus(lastUpdated, thresholds);
        if (status === 'red') {
          notifications.push({
            id: `stale_${mod}`,
            type: 'staleData',
            category: 'warning',
            titleKey: 'notifications.staleData',
            titleParams: { module: mod },
            descKey: 'notifications.staleDataDesc',
            descParams: {},
            route: MODULE_ROUTES[mod] || '/',
            daysUntil: null,
          });
        }
      });
    } catch (e) { /* skip */ }

    // 3. Persisted assignee notifications (e.g. scheduled report auto-created)
    try {
      const persisted = storage.getJSON(ASSIGNEE_NOTIFICATIONS_KEY, []);
      if (persisted.length > 0) {
        const currentUser = auditService.getCurrentUser();
        const username = (currentUser.username || '').toLowerCase();
        for (const n of persisted) {
          const assignees = n.assignees || [];
          if (assignees.length === 0 || assignees.includes(username)) {
            notifications.push({
              id: n.id,
              type: 'assigneeNotifications',
              category: n.category || 'info',
              titleKey: n.titleKey,
              titleParams: n.titleParams || {},
              descKey: n.descKey,
              descParams: n.descParams || {},
              route: n.route,
              daysUntil: null,
            });
          }
        }
      }
    } catch (e) { /* skip */ }

    // 4. KPI threshold breaches
    try {
      const geoStats = getGeoRiskStats();
      const bwraStats = getBwraStats();
      const prptStats = getPeriodicReportingStats();
      const breaches = kpiThresholdService.getBreachedKpis({
        geo_highRisk: geoStats.highRisk,
        bwra_unmitigated: bwraStats.unmitigated,
        prpt_review: prptStats.review,
      });
      breaches.forEach(({ key, value, threshold }) => {
        notifications.push({
          id: `kpi_${key}`,
          type: 'kpiThresholds',
          category: 'warning',
          titleKey: 'notifications.kpiThreshold',
          titleParams: { kpi: key },
          descKey: 'notifications.kpiThresholdDesc',
          descParams: { value, threshold },
          route: '/dashboard',
          daysUntil: null,
        });
      });
    } catch (e) { /* skip */ }

    // Sort: urgent first, then warnings, then info; within each by daysUntil
    notifications.sort((a, b) => {
      const order = { urgent: 0, warning: 1, info: 2 };
      const catDiff = order[a.category] - order[b.category];
      if (catDiff !== 0) return catDiff;
      if (a.daysUntil !== null && b.daysUntil !== null) return a.daysUntil - b.daysUntil;
      return 0;
    });

    return notifications;
  }

  _filterByRole(notifications) {
    try {
      const currentUser = auditService.getCurrentUser();
      const roleSettings = this.getRoleSettings(currentUser.role);
      return notifications.filter(n => {
        if (!n.type) return true;
        const cfg = roleSettings[n.type];
        return !cfg || cfg.enabled !== false;
      });
    } catch (e) {
      return notifications;
    }
  }

  getNotifications() {
    const readStatus = this._getReadStatus();
    return this._filterByRole(this._computeRaw()).map(n => ({ ...n, read: !!readStatus[n.id] }));
  }

  getUnreadCount() {
    const readStatus = this._getReadStatus();
    return this._filterByRole(this._computeRaw()).filter(n => !readStatus[n.id]).length;
  }

  async markAsRead(id) {
    const status = this._getReadStatus();
    status[id] = true;
    await this._setReadStatus(status);
    this.notifyListeners();
  }

  async markAllAsRead() {
    const status = {};
    this._computeRaw().forEach(n => { status[n.id] = true; });
    await this._setReadStatus(status);
    this.notifyListeners();
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(l => { try { l(); } catch (e) { /* ignore */ } });
  }

  // === NOTIFICATION SETTINGS PER ROLE ===

  getNotificationSettings() {
    return storage.getJSON(NOTIFICATION_SETTINGS_KEY, {});
  }

  getRoleSettings(roleId) {
    const all = this.getNotificationSettings();
    if (all[roleId]) return all[roleId];
    return DEFAULT_ROLE_SETTINGS(roleId);
  }

  async saveRoleSettings(roleId, settings) {
    const all = this.getNotificationSettings();
    all[roleId] = settings;
    await storage.setJSON(NOTIFICATION_SETTINGS_KEY, all);
    this.notifyListeners();
  }

  async resetRoleSettings(roleId) {
    const all = this.getNotificationSettings();
    delete all[roleId];
    await storage.setJSON(NOTIFICATION_SETTINGS_KEY, all);
    this.notifyListeners();
  }

  // === WEBHOOK SETTINGS ===

  getWebhookSettings() {
    return storage.getJSON(WEBHOOK_SETTINGS_KEY, { url: '', events: {} });
  }

  async saveWebhookSettings(settings) {
    await storage.setJSON(WEBHOOK_SETTINGS_KEY, settings);
  }

  /**
   * Dispatch an event to configured webhook (fire-and-forget).
   * Called by services when important events occur.
   * @param {string} eventType - one of WEBHOOK_EVENT_TYPES[].id
   * @param {object} payload - event-specific data
   */
  async dispatchWebhook(eventType, payload = {}) {
    try {
      const { url, events } = this.getWebhookSettings();
      if (!url) return;
      const eventCfg = events[eventType];
      // default enabled unless explicitly disabled
      if (eventCfg && eventCfg.enabled === false) return;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: eventType,
          payload,
          timestamp: new Date().toISOString(),
          source: 'prime-rms',
        }),
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  }

  /**
   * Emit a named event: dispatches webhook and notifies in-app listeners.
   * @param {string} eventType - one of WEBHOOK_EVENT_TYPES[].id
   * @param {object} payload - event-specific data
   */
  async emit(eventType, payload = {}) {
    await this.dispatchWebhook(eventType, payload);
    this.notifyListeners();
  }

  /**
   * Persist a notification targeted at specific assignees (by username).
   * Called by services (e.g. PeriodicReportingService) after auto-creating
   * a scheduled report or distributing an approved report.
   * The notification appears for each listed assignee when they view their notification panel.
   *
   * @param {{ id: string, year: number, route: string, assignees: string,
   *           titleKey?: string, titleParams?: object,
   *           descKey?: string, descParams?: object, category?: string }} opts
   *   assignees — comma-separated usernames; empty string means notify all users
   */
  async addAssigneeNotification({ id, year, route, assignees,
    titleKey, titleParams, descKey, descParams, category }) {
    const existing = storage.getJSON(ASSIGNEE_NOTIFICATIONS_KEY, []);
    if (existing.some(n => n.id === id)) return; // idempotent
    const assigneeList = (assignees || '')
      .split(',')
      .map(a => a.trim().toLowerCase())
      .filter(Boolean);
    existing.push({
      id,
      category: category || 'info',
      titleKey: titleKey || 'notifications.scheduledReportCreated',
      titleParams: titleParams !== undefined ? titleParams : { year },
      descKey: descKey || 'notifications.scheduledReportCreatedDesc',
      descParams: descParams !== undefined ? descParams : { year },
      route,
      assignees: assigneeList,
      createdAt: new Date().toISOString(),
    });
    await storage.setJSON(ASSIGNEE_NOTIFICATIONS_KEY, existing);
    this.notifyListeners();
  }
}

const notificationService = new NotificationService();
export default notificationService;
